import { useState, useEffect } from "react";
import { getPublicPricingPlans } from "../../../services/paymentService";
import { shouldUsePaystack, getUserRegion } from "../../../utils/locationUtils";

const PricingTable = () => {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usePaystack, setUsePaystack] = useState(false);
  const [userRegion, setUserRegion] = useState('');
  // Fetch pricing plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      let shouldUsePaystackProvider = false;
      
      try {
        setLoading(true);
        setError(null);
        
        // Detect location and payment provider
        shouldUsePaystackProvider = await shouldUsePaystack();
        setUsePaystack(shouldUsePaystackProvider);
        const region = await getUserRegion();
        setUserRegion(region);
        
        // Fetch plans based on detected provider
        const plansData = await getPublicPricingPlans(shouldUsePaystackProvider);
        // Ensure plans is an array
        const plans = Array.isArray(plansData) ? plansData : (plansData ? [plansData] : []);
        
        // Define the Free Playground plan (always shown first)
        const freePlaygroundPlan = {
          name: "Free Playground",
          price: 0,
          tokens: "Limited",
          characters: "Rate Limited Access",
          id: "free-playground",
        };
        
        // Check if Free Playground already exists in the API response
        const hasFreePlayground = plans.some(plan => 
          (plan.name || "").toLowerCase().includes("free playground") || 
          ((plan.name || "").toLowerCase().includes("free") && (plan.price === 0 || plan.price === "$0" || plan.amount === 0))
        );
        
        // Add Free Playground plan if it doesn't exist
        const allPlans = hasFreePlayground ? plans : [freePlaygroundPlan, ...plans];
        
        // Transform API response to table format
        const transformedPlans = allPlans.map((plan) => {
          const planNameLower = (plan.name || "").toLowerCase();
          const isFreePlan = planNameLower.includes("free playground") || 
                            (planNameLower.includes("free") && (plan.price === 0 || plan.price === "$0" || plan.amount === 0));
          
          // Get plan price for formatting and sorting
          const planPrice = plan.price || plan.amount || plan.priceAmount;
          
          // Format price - Free Playground shows $0 or ₦0 based on provider
          let price;
          if (isFreePlan) {
            price = shouldUsePaystackProvider ? "₦0" : "$0";
          } else {
            if (typeof planPrice === 'number') {
              if (shouldUsePaystackProvider) {
                // Paystack: price is in kobo, convert to naira
                const nairaAmount = planPrice / 100;
                price = `₦${nairaAmount.toLocaleString()}`;
              } else {
                // Stripe: price is in dollars
                price = `$${planPrice}`;
              }
            } else if (planPrice) {
              // If price is already a string, check if it needs conversion
              if (typeof planPrice === 'string') {
                // If it already has currency symbol, use as is
                if (planPrice.includes('$') || planPrice.includes('₦')) {
                  price = planPrice;
                } else {
                  // Try to parse and format
                  const numPrice = parseFloat(planPrice.replace(/[^\d.]/g, ''));
                  if (!isNaN(numPrice)) {
                    if (shouldUsePaystackProvider) {
                      const nairaAmount = numPrice / 100; // Assume kobo if Paystack
                      price = `₦${nairaAmount.toLocaleString()}`;
                    } else {
                      price = `$${numPrice}`;
                    }
                  } else {
                    price = planPrice;
                  }
                }
              } else {
                price = planPrice;
              }
            } else {
              price = "Custom";
            }
          }
          
          // Format tokens
          let token;
          let tokensNumber = 0;
          if (isFreePlan) {
            token = "Limited";
          } else if (plan.tokens) {
            tokensNumber = typeof plan.tokens === 'number' 
              ? plan.tokens 
              : typeof plan.tokens === 'string' 
                ? parseInt(plan.tokens.replace(/,/g, '')) || 0
                : 0;
            token = tokensNumber > 0 ? tokensNumber.toLocaleString() : plan.tokens;
          } else if (plan.token) {
            token = plan.token;
            tokensNumber = typeof plan.token === 'string' 
              ? parseInt(plan.token.replace(/,/g, '')) || 0
              : 0;
          } else {
            token = "Custom";
          }
          
          // Format characters - calculate from tokens if not provided (tokens * 4)
          let characters;
          if (isFreePlan) {
            characters = "Rate Limited Access";
          } else if (plan.characters) {
            const charsNumber = typeof plan.characters === 'number' 
              ? plan.characters 
              : typeof plan.characters === 'string' 
                ? parseInt(plan.characters.replace(/,/g, '')) || 0
                : 0;
            characters = charsNumber > 0 ? charsNumber.toLocaleString() : plan.characters;
          } else if (plan.characterCount) {
            const charsNumber = typeof plan.characterCount === 'number' 
              ? plan.characterCount 
              : typeof plan.characterCount === 'string' 
                ? parseInt(plan.characterCount.replace(/,/g, '')) || 0
                : 0;
            characters = charsNumber > 0 ? charsNumber.toLocaleString() : plan.characterCount;
          } else if (tokensNumber > 0) {
            // Calculate characters from tokens (1 token = 4 characters)
            const calculatedChars = Math.round(tokensNumber * 4);
            characters = calculatedChars.toLocaleString();
          } else {
            characters = "See details";
          }

          return {
            plan: plan.name || plan.planName || "Plan",
            price,
            token,
            characters,
            // Store numeric price for sorting
            numericPrice: isFreePlan ? 0 : (typeof planPrice === 'number' ? planPrice : (typeof planPrice === 'string' ? parseFloat(planPrice.replace(/[^\d.]/g, '')) || Infinity : Infinity))
          };
        });

        // Sort plans by price (lowest first)
        const sortedPlans = transformedPlans.sort((a, b) => {
          // Extract numeric price from formatted price strings
          const extractNumericPrice = (priceStr) => {
            if (!priceStr) return Infinity; // Custom/unknown prices go to end
            
            // Extract number from strings like "$10", "₦1000", "$299+", "₦418,600+", etc.
            // Remove all non-digit characters except decimal point, then parse
            const numericStr = priceStr.replace(/[^\d.]/g, '');
            const num = parseFloat(numericStr);
            
            // If includes "+" (like $299+), extract the number before the +
            if (priceStr.includes('+')) {
              return isNaN(num) ? Infinity : num;
            }
            
            return isNaN(num) ? Infinity : num;
          };
          
          const priceA = extractNumericPrice(a.price);
          const priceB = extractNumericPrice(b.price);
          
          return priceA - priceB;
        });

        setPricingPlans(sortedPlans);
      } catch (err) {
        console.error("Error fetching pricing plans:", err);
        setError(err.message || "Failed to load pricing plans. Please try again later.");
        
        // Fallback to default plans if API fails (use detected provider or default to Stripe)
        setPricingPlans([
          {
            plan: "Free Playground",
            price: shouldUsePaystackProvider ? "₦0" : "$0",
            token: "Limited",
            characters: "Rate Limited Access"
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return (
    <div className="min-h-screen bg-white p-8 mt-10">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-8">
            Plan Overview
          </h1>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-auto max-w-3xl mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary"></div>
          </div>
        )}

        {/* Pricing Table */}
        {!loading && pricingPlans.length > 0 && (
          <div className="bg-card rounded-lg border shadow-sm">
            <table className="w-full text-left border border-[#cbd1ce]">
              <thead>
                <tr className="bg-[#F4F8F7] font-semibold">
                  <th className="font-bold text-foreground uppercase tracking-wider p-4 border border-[#cbd1ce]">PLAN</th>
                  <th className="font-bold text-foreground uppercase tracking-wider p-4 border border-[#cbd1ce]">PRICE</th>
                  <th className="font-bold text-foreground uppercase tracking-wider p-4 border border-[#cbd1ce]">TOKEN</th>
                  <th className="font-bold text-foreground uppercase tracking-wider p-4 border border-[#cbd1ce]">CHARACTERS</th>
                </tr>
              </thead>
              <tbody>
                {pricingPlans.map((plan, index) => (
                  <tr key={plan.plan || index} className="hover:bg-muted/30 border border-[#cbd1ce]">
                    <td className="font-medium text-foreground p-4 border border-[#cbd1ce]">{plan.plan}</td>
                    <td className="text-foreground p-4 border border-[#cbd1ce]">{plan.price}</td>
                    <td className="text-foreground p-4 border border-[#cbd1ce]">{plan.token}</td>
                    <td className="text-foreground p-4 border border-[#cbd1ce]">{plan.characters}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && pricingPlans.length === 0 && !error && (
          <div className="text-center py-20">
            <p className="text-muted-foreground">No pricing plans available at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingTable;